import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axios from 'axios';
import UpgradeModal from './UpgradeModal';

const PlanLimitContext = createContext(null);

/**
 * Globally captures axios 409 limit_reached responses and surfaces an
 * UpgradeModal. Components can also use the hook to open the modal manually
 * (e.g. from a "Add user" button when the user is already at limit).
 */
export const PlanLimitProvider = ({ children }) => {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);

  const showLimit = useCallback((data) => {
    if (!data) return;
    setInfo(data);
    setOpen(true);
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    // keep info around briefly so the dialog fades out cleanly
    setTimeout(() => setInfo(null), 250);
  }, []);

  useEffect(() => {
    // Single global response interceptor for plan-limit handling.
    const id = axios.interceptors.response.use(
      (r) => r,
      (error) => {
        const detail = error?.response?.data?.detail;
        if (
          error?.response?.status === 409 &&
          typeof detail === 'object' &&
          detail?.code === 'limit_reached'
        ) {
          showLimit(detail);
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [showLimit]);

  const value = useMemo(() => ({ showLimit, close }), [showLimit, close]);

  return (
    <PlanLimitContext.Provider value={value}>
      {children}
      <UpgradeModal info={info} open={open} onClose={close} />
    </PlanLimitContext.Provider>
  );
};

export const usePlanLimit = () => {
  const ctx = useContext(PlanLimitContext);
  if (!ctx) {
    // No-op fallback if used outside provider — keep app running
    return { showLimit: () => {}, close: () => {} };
  }
  return ctx;
};

export default PlanLimitProvider;
